import type { CustomTemplate } from '../types';

// An SVG template is drawn whole and centred inside the sticker box (letterboxed), while its text and icon
// zones are percentages of the box. In a box of any other proportions the zones drift away from the artwork,
// down to text landing in the empty strip outside the cut line — so an SVG template's box has to keep the
// artwork's proportions. PNG templates are cropped to fill the box and are left free.

// Absolute CSS lengths of the SVG root's width/height, converted to px.
const UNIT_TO_PX: Record<string, number> = { '': 1, px: 1, pt: 4 / 3, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, in: 96 };

const lengthToPx = (raw: string | undefined): number | null => {
    const m = raw?.trim().match(/^([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)([a-z]*)$/i);
    const factor = m ? UNIT_TO_PX[m[2].toLowerCase()] : undefined;
    if (!m || factor === undefined) return null; // %, em, … give no intrinsic size
    const px = parseFloat(m[1]) * factor;
    return px > 0 ? px : null;
};

const ROOT_TAG = /<svg\b(?:[^>"']|"[^"]*"|'[^']*')*>/i;

const attribute = (tag: string, name: string): string | undefined =>
    tag.match(new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i'))?.slice(1).find(v => v !== undefined);

const decodeDataUri = (dataUri: string): string | null => {
    const comma = dataUri.indexOf(',');
    if (comma < 0) return null;
    try {
        return /;base64/i.test(dataUri.slice(0, comma)) ? atob(dataUri.slice(comma + 1)) : decodeURIComponent(dataUri.slice(comma + 1));
    } catch {
        return null;
    }
};

const ratioCache = new Map<string, number | null>();

/**
 * Height / width of an SVG data URI as the browser lays it out: from the root's width and height when both are
 * absolute lengths, otherwise from its viewBox. Null when the file states neither.
 */
export function svgAspectRatio(dataUri: string): number | null {
    if (ratioCache.has(dataUri)) return ratioCache.get(dataUri)!;
    let ratio: number | null = null;
    const tag = decodeDataUri(dataUri)?.match(ROOT_TAG)?.[0];
    if (tag) {
        const w = lengthToPx(attribute(tag, 'width'));
        const h = lengthToPx(attribute(tag, 'height'));
        if (w && h) {
            ratio = h / w;
        } else {
            const box = attribute(tag, 'viewBox')?.trim().split(/[\s,]+/).map(Number);
            if (box?.length === 4 && box[2] > 0 && box[3] > 0) ratio = box[3] / box[2];
        }
    }
    ratioCache.set(dataUri, ratio);
    return ratio;
}

/**
 * The only height (mm) an SVG template may have at the given width, rounded like the height a template
 * gets when it is picked. Null for anything that stays free: no template, a PNG/JPG one, or an SVG that
 * states no proportions.
 */
export function lockedTemplateHeight(template: Pick<CustomTemplate, 'data'> | undefined, widthMm: number): number | null {
    if (!template?.data?.startsWith('data:image/svg+xml')) return null;
    const ratio = svgAspectRatio(template.data);
    return ratio ? Math.max(3, Math.round(widthMm * ratio * 10) / 10) : null;
}
